import React, { useCallback, useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { IconButton, Typography } from '@material-ui/core';
import TableView from '../../TableView/TableView';
import EditIcon from '@material-ui/icons/Edit';
import AddBoxIcon from '@material-ui/icons/AddBox';
//imports for edit Equipment
import {
  clearPaginationInfo,
  getEntities as getEquipments,
  getEquipmentSerialNumbers as getSerials,
  setPage,
  setRowsPerPage,
} from '../../../redux/actions/equipmentsActions';
import EquipmentEditSpecificData from './EquipmentEditSpecificData';
import EquipmentCreateEditSpecificData from './EquipmentCreateEditSpecificData';
import { getEntities as getCategories } from '../../../redux/actions/categoriesActions';
import { getEntities as getSuppliers } from '../../../redux/actions/suppliersActions';
import { getEntities as getModels } from '../../../redux/actions/modelsActions';
import { getEntities as getZones } from '../../../redux/actions/restautantZones';
import { disableAssignmentButton } from '../../../redux/actions/utilsActions';
import { selectModelsName } from '../../../redux/selectors/modelsSelector';
import ColumnInterface from '../../../interfaces/column';
import RBISearchButton from '../../RBISearchButton/RBISearchButton';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { textToSnakeCase, formatYYYYMMDD } from '../../../utils/utils';

/**
 * Table displaying equipment assigned to restaurant
 * @param restaurantId
 * @returns restaurant equipment table
 */
function EquipmentTabRestaurant({ restaurantId }) {
  const dispatch: any = useDispatch();
  const { t } = useTranslation();
  const {
    restaurantZones,
    selectedBrand,
    paginationConfig,
    thisUser,
    equipments,
    models,
    searchReduxObject,
    categories,
    suppliers,
    serialNumbers,
  } = useSelector((state: RootStateOrAny) => ({
    thisUser: state.UserReducer.thisUser,
    equipments: state.EquipmentsReducer.entities,
    models: selectModelsName(state),
    searchReduxObject: state.EquipmentsReducer.searchObject,
    categories: state.CategoriesReducer.entities,
    suppliers: state.SuppliersReducer.entities,
    serialNumbers: state.EquipmentsReducer.serialNumbers,
    restaurantZones: state.RestaurantZonesReducer.entities,
    paginationConfig: state.EquipmentsReducer.paginationConfig,
    selectedBrand: state.UIReducer.selectedBrand,
  }));
  const [equipmentsTreated, setEquipmentsTreated] = useState([]);
  /////////////handle edit equipment details
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [pieceToEdit, setPieceToEdit] = useState<any>({});
  const handleClickOpen = equipmentPieceId => async () => {
    const equipmentToEdit = await equipments.find(
      equipment => equipment.equipmentPieceId === equipmentPieceId,
    );
    setPieceToEdit(equipmentToEdit);
    setEditModalOpen(true);
  };

  //////////////////
  const [openCreateModal, setOpenCreateModal] = useState(false);
  useEffect(() => {
    setEquipmentsTreated(
      equipments.map(e => {
        const newEquipment = { ...e };
        newEquipment.action = (
          <IconButton
            onClick={handleClickOpen(e.equipmentPieceId)}
            disabled={
              !thisUser.hasEditSpecificEquipmentFields ||
              e.equipmentPieceId === null
            }
          >
            <EditIcon />
          </IconButton>
        );
        newEquipment.visible = e.is_visible_for_franchise
          ? t('equipment.visible')
          : t('equipment.nonVisible');
        newEquipment.core = e.is_core
          ? t('equipment.core')
          : t('equipment.nonCore');
        newEquipment.status = e.is_active
          ? t('app.common.active')
          : t('app.common.inactive');
        newEquipment.approval_type = e.approval_type?.name;
        newEquipment.category = t(
          `equipment.categories.${textToSnakeCase(e.equipment_category?.name)}`,
          e.equipment_category?.name,
        );
        newEquipment.supplier = e.manufacturer?.name;
        newEquipment.restaurant_zone =
          e.restaurant_zone && e.restaurant_zone.name
            ? t(
                `equipment.zone.${textToSnakeCase(e.restaurant_zone?.name)}`,
                e.restaurant_zone?.name,
              )
            : '';
        newEquipment.alternate_name = e.alternate_name;
        newEquipment.serial_number = e.serial_number;
        newEquipment.equipment_purchase_date = e.equipment_purchase_date
          ? moment(e.equipment_purchase_date).format(formatYYYYMMDD)
          : e.equipment_purchase_date;
        return newEquipment;
      }),
    );
  }, [equipments]);
  const [searchObject, setSearchObject] = useState<any>({
    availability: '',
    category: '',
    supplier: '',
    model: '',
    status: '',
    core: '',
    franchise: '',
    type: '',
    serialNumber: '',
    restaurantZoneId: '',
    description: '',
  });
  const [searchObjectPrev, setSearchObjectPrev] = useState<any>({
    availability: '',
    category: '',
    supplier: '',
    model: '',
    status: '',
    core: '',
    franchise: '',
    type: '',
    serialNumber: '',
    restaurantZoneId: '',
    description: '',
  });
  useEffect(() => {
    if (selectedBrand) {
      dispatch(clearPaginationInfo());
      dispatch(
        getEquipments({
          brand: selectedBrand,
          page: 1,
          limit: paginationConfig.rowsPerPage,
          restaurantId,
        }),
      );
      dispatch(
        getCategories(
          {
            brand: selectedBrand,
            restaurantId,
          },
          {
            _disableRequestStatus: true,
          },
        ),
      );
      dispatch(
        getModels(
          {
            brand: selectedBrand,
            restaurantId,
            all: true,
          },
          {
            _disableRequestStatus: true,
          },
        ),
      );
      dispatch(
        getSerials(
          {
            restaurantId,
            selectedBrand,
            page: 1,
            limit: 10,
          },
          {
            _disableRequestStatus: true,
          },
        ),
      );
      dispatch(
        getSuppliers(
          {
            brand: selectedBrand,
            restaurantId: restaurantId,
          },
          {
            _disableRequestStatus: true,
          },
        ),
      );
      dispatch(
        getZones(selectedBrand, {
          _disableRequestStatus: true,
        }),
      );
    }
  }, [selectedBrand]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchObjectPrev({
      ...searchObject,
      [event.target.name]: event.target.value,
    });
  };

  const handleChangeAutocomplete = (name: string, value: any) => {
    setSearchObjectPrev({
      ...searchObject,
      [name]: value ? value : '',
    });
    if (!value) {
      setSearchObject({
        ...searchObject,
        [name]: value ? value : '',
      });
    }
  };

  const [serialInput, setSerialInput] = useState<string>('');
  const [timer, setTimer] = useState<any>(null);

  const callBackFunctionSerialNumbers = useCallback(
    value => {
      clearTimeout(timer);
      if (serialInput !== value && (value.length > 2 || value === '')) {
        setSerialInput(value);
        setTimer(
          setTimeout(
            () =>
              dispatch(
                getSerials({
                  name: value,
                  restaurantId,
                  selectedBrand,
                  page: 1,
                  limit: 10,
                }),
              ),
            600,
          ),
        );
      }
    },
    [timer, setSerialInput, setTimer],
  );

  const [modelInput, setModelInput] = useState<string>('');

  const callBackFunctionModels = useCallback(
    value => {
      clearTimeout(timer);
      if (modelInput !== value && (value.length > 0 || value === '')) {
        setModelInput(value);
        setTimer(
          setTimeout(
            () =>
              dispatch(
                getModels({ brand: selectedBrand, name: value || null }),
              ),
            1000,
          ),
        );
      }
    },
    [timer, setModelInput, setTimer],
  );

  const handleInputChangeAutocomplete = (name: string, value: any) => {
    if (name === 'serialNumber') {
      callBackFunctionSerialNumbers(value);
    }

    if (name === 'model' && value) {
      callBackFunctionModels(value);
    }
  };

  /** Handle filter apply button click */
  const handleApplyButton = () => {
    setSearchObject({ ...searchObjectPrev });
    dispatch(setPage(1));
    dispatch(
      getEquipments({
        ...searchReduxObject,
        page: 1,
        limit: paginationConfig.rowsPerPage,
        brand: selectedBrand,
        ...searchObjectPrev,
        type: searchObjectPrev.type.id,
        category: searchObjectPrev.category.id,
        supplier: searchObjectPrev.supplier.id,
        model: searchObjectPrev.model.name,
        restaurantId,
        serialNumber: searchObjectPrev.serialNumber.name,
        restaurantZoneId: searchObjectPrev.restaurantZoneId.id,
      }),
    );
    dispatch(
      getSuppliers({
        brand: selectedBrand,
        restaurantId: restaurantId,
        category: searchObjectPrev.category.id,
      }),
    );
  };

  const handleOnClosePopUp = () => {
    setSearchObjectPrev({
      ...searchObject,
    });
  };

  useEffect(() => {
    setSearchObjectPrev({
      ...searchObject,
    });
  }, [
    searchObject.availability,
    searchObject.category,
    searchObject.supplier,
    searchObject.model,
    searchObject.restaurantZoneId,
    searchObject.description,
  ]);

  const handleOpenCreateModal = () => {
    setOpenCreateModal(true);
  };
  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
  };
  const columns: ColumnInterface[] = [
    {
      id: 'category',
      label: (
        <RBISearchButton
          id={'category'}
          valueOption={searchObjectPrev['category']}
          labelText={t('operationHours.category')}
          onChange={handleChangeAutocomplete}
          handleApplyButton={handleApplyButton}
          handleOnClosePopUp={handleOnClosePopUp}
          options={categories.map(option => ({
            ...option,
            name: t(
              `equipment.categories.${textToSnakeCase(option.name)}`,
              option.name,
            ),
          }))}
        />
      ),
      align: 'left',
      paddingRight: 20,
    },
    {
      id: 'supplier',
      label: (
        <RBISearchButton
          id={'supplier'}
          valueOption={searchObjectPrev['supplier']}
          labelText={t('operationHours.supplier')}
          onChange={handleChangeAutocomplete}
          handleApplyButton={handleApplyButton}
          handleOnClosePopUp={handleOnClosePopUp}
          options={suppliers}
        />
      ),
      align: 'left',
      paddingRight: 20,
    },
    {
      id: 'model',
      label: (
        <RBISearchButton
          id={'model'}
          valueOption={searchObjectPrev['model']}
          labelText={t('operationHours.model')}
          onChange={handleChangeAutocomplete}
          onChangeInput={handleInputChangeAutocomplete}
          handleApplyButton={handleApplyButton}
          handleOnClosePopUp={handleOnClosePopUp}
          options={models}
        />
      ),
      align: 'left',
      paddingRight: 20,
    },
    {
      id: 'description',
      label: (
        <RBISearchButton
          id={'description'}
          valueOption={searchObjectPrev['description']}
          labelText={t('equipment.description')}
          noOptions={true}
          onChange={handleChange}
          handleApplyButton={handleApplyButton}
          handleOnClosePopUp={handleOnClosePopUp}
        />
      ),
      align: 'left',
      paddingRight: 20,
    },
    {
      id: 'serial_number',
      label: (
        <RBISearchButton
          id={'serialNumber'}
          valueOption={searchObjectPrev['serialNumber']}
          labelText={t('operationHours.serialNumber')}
          onChange={handleChangeAutocomplete}
          onChangeInput={handleInputChangeAutocomplete}
          handleApplyButton={handleApplyButton}
          handleOnClosePopUp={handleOnClosePopUp}
          options={
            serialNumbers?.serialNumbers?.length
              ? serialNumbers?.serialNumbers?.map(serial => ({
                  name: serial.serial_number,
                  value: serial.serial_number,
                }))
              : []
          }
        />
      ),
      align: 'left',
      paddingRight: 20,
    },
    {
      id: 'alternate_name',
      label: <Typography>{t('operationHours.alternateName')}</Typography>,
      align: 'left',
      minWidth: 150,
      paddingRight: 20,
    },
    {
      id: 'restaurant_zone',
      label: (
        <RBISearchButton
          id={'restaurantZoneId'}
          valueOption={searchObjectPrev['restaurantZoneId']}
          labelText={t('restaurant.info.equipment.zone')}
          onChange={handleChangeAutocomplete}
          handleOnClosePopUp={handleOnClosePopUp}
          width={200}
          options={restaurantZones.map(zone => ({
            name: t(`equipment.zone.${textToSnakeCase(zone.name)}`, zone.name),
            id: zone.id,
          }))}
          handleApplyButton={handleApplyButton}
        />
      ),
      align: 'left',
      paddingRight: 20,
    },
    {
      id: 'equipment_purchase_date',
      label: <Typography>{t('createModel.DateOfPurchase')}</Typography>,
      align: 'left',
      minWidth: 150,
      paddingRight: 20,
    },
    {
      id: 'action',
      label: (
        <IconButton
          disabled={!thisUser.hasEditSpecificEquipmentFields}
          onClick={handleOpenCreateModal}
        >
          <AddBoxIcon />
        </IconButton>
      ),
      paddingRight: 20,
    },
  ];

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(1));
    dispatch(
      getEquipments({
        ...searchReduxObject,
        page: 1,
        limit: +event.target.value,
        brand: selectedBrand,
        ...searchObject,
        type: searchObject.type.id,
        category: searchObject.category.id,
        supplier: searchObject.supplier.id,
        model: searchObject.model.name,
        serialNumber: searchObject.serialNumber,
        restaurantId,
      }),
    );
  };

  const handleRefreshOnCreate = () => {
    dispatch(setPage(1));
    dispatch(
      getEquipments({
        brand: selectedBrand,
        page: 1,
        limit: paginationConfig.rowsPerPage,
        restaurantId,
      }),
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(
      getEquipments({
        ...searchReduxObject,
        page: newPage + 1,
        limit: paginationConfig.rowsPerPage,
        brand: selectedBrand,
        ...searchObject,
        type: searchObject.type.id,
        category: searchObject.category.id,
        supplier: searchObject.supplier.id,
        model: searchObject.model.name,
        restaurantId,
      }),
    );
  };
  useEffect(() => {
    return () => {
      dispatch(disableAssignmentButton());
    };
  }, [dispatch]);

  return (
    <div>
      {editModalOpen && (
        <EquipmentEditSpecificData
          open={editModalOpen}
          handleClose={setEditModalOpen}
          restaurantId={restaurantId}
          pieceToEdit={pieceToEdit}
          handleRefreshOnCreate={handleRefreshOnCreate}
        />
      )}
      <TableView
        columns={columns}
        rows={equipmentsTreated}
        paginationConfig={paginationConfig}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleChangePage={handleChangePage}
        messageEmptyArray={t('operationHours.noEquipmentAvailable')}
      />
      <EquipmentCreateEditSpecificData
        open={openCreateModal}
        handleClose={handleCloseCreateModal}
        restaurantId={restaurantId}
        handleRefreshOnCreate={handleRefreshOnCreate}
      />
    </div>
  );
}

export default EquipmentTabRestaurant;
